import React, { useState, useEffect } from 'react';
import { 
  X, 
  Search, 
  Check, 
  LayoutGrid, 
  MapPin, 
  Phone, 
  MessageCircle, 
  Clock, 
  User, 
  Building2, 
  Store,
  Image as ImageIcon, 
  Sparkles, 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle2, 
  Share2, 
  Compass, 
  AlertCircle
} from 'lucide-react';
import ImageUpload from './ImageUpload';
import CustomSelect from './CustomSelect';
import api from '../utils/api';
import { getCityFromHost } from '../utils/getCityFromHost';
import { showSuccess, showError } from '../utils/toast';
import { useSubmitBusiness } from '../contexts/SubmitBusinessContext';

const CITY_PINCODES = {
  fatehabad: '125050',
  tohana: '125120',
  ratia: '125051',
  bhuna: '125111',
  bhattu: '125053',
  jakhal: '125123',
  uklana: '125113',
  ukalana: '125113',
  hisar: '125001',
  sirsa: '125055'
};

const STEPS = [
  { id: 1, title: 'बुनियादी जानकारी', subtitle: 'Basic Details', icon: Building2 },
  { id: 2, title: 'संपर्क व समय', subtitle: 'Contact & Timings', icon: Phone },
  { id: 3, title: 'स्थान व पता', subtitle: 'Location & Address', icon: MapPin },
  { id: 4, title: 'फोटो व प्रीव्यू', subtitle: 'Photos & Preview', icon: Sparkles }
];

const SubmitBusinessModal = ({ isOpen, onClose }) => {
  const city = getCityFromHost();
  const { initialData } = useSubmitBusiness();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedBusiness, setSubmittedBusiness] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    category: '',
    subcategory: '',
    phone: '',
    whatsapp: '',
    sameAsPhone: true,
    email: '',
    website: '',
    openingTime: '09:00 AM',
    closingTime: '08:00 PM',
    isOpenSunday: true,
    city: '',
    village: '',
    landmark: '',
    address: '',
    pincode: '',
    description: '',
  });

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [cities, setCities] = useState([]);
  const [villages, setVillages] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stepErrors, setStepErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setIsSuccess(false);
      setSubmittedBusiness(null);
      setStepErrors({});

      const fetchData = async () => {
        try {
          const [catsRes, citiesRes, villagesRes, tagsRes] = await Promise.all([
            api.get('/categories'),
            api.get('/cities'),
            api.get('/villages'),
            api.get('/tags'),
          ]);
          setCategories(catsRes.data || []);
          setCities(citiesRes.data || []);
          setAllTags(tagsRes.data || []);

          const vList = (villagesRes.data || []).map(v => ({
            _id: v._id,
            name: `${v.name} (${v.block} Block)`
          }));
          setVillages(vList);

          // If city is detected from subdomain, set it and its default pincode
          let detectedCityId = '';
          let detectedSlug = '';
          if (city) {
            const detectedCity = (citiesRes.data || []).find(
              c => c.domain === city || c.slug === city
            );
            if (detectedCity) {
              detectedCityId = detectedCity._id;
              detectedSlug = detectedCity.slug;
            }
          }

          setFormData(prev => ({
            ...prev,
            city: detectedCityId || prev.city,
            pincode: (detectedSlug && CITY_PINCODES[detectedSlug]) || prev.pincode || '125050',
            village: initialData?.village || prev.village || ''
          }));
        } catch (error) {
          console.error('Error fetching initial modal data:', error);
        }
      };

      fetchData();
    }
  }, [isOpen, city, initialData]);

  // Load subcategories when category changes
  useEffect(() => {
    if (formData.category) {
      const fetchSubcategories = async () => {
        try {
          const response = await api.get(`/subcategories?categoryId=${formData.category}`);
          setSubcategories(response.data || []);
        } catch (error) {
          console.error('Error fetching subcategories:', error);
          setSubcategories([]);
        }
      };
      fetchSubcategories();
    } else {
      setSubcategories([]);
    }
  }, [formData.category]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const updated = { ...prev, [name]: val };

      // If user toggles "sameAsPhone", sync whatsapp with phone
      if (name === 'sameAsPhone' && val) {
        updated.whatsapp = updated.phone;
      }
      if (name === 'phone' && prev.sameAsPhone) {
        updated.whatsapp = value;
      }

      // If city changed, auto-suggest pincode
      if (name === 'city') {
        const foundCity = cities.find(c => c._id === value);
        if (foundCity && CITY_PINCODES[foundCity.slug]) {
          updated.pincode = CITY_PINCODES[foundCity.slug];
        }
      }

      return updated;
    });

    // Clear error for this field
    if (stepErrors[name]) {
      setStepErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateStep = (step) => {
    const errors = {};

    if (step === 1) {
      if (!formData.name.trim()) errors.name = 'व्यवसाय/दुकान का नाम आवश्यक है (Business Name is required)';
      if (!formData.category) errors.category = 'कृपया एक मुख्य श्रेणी चुनें (Category is required)';
    }

    if (step === 2) {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (!phoneDigits) {
        errors.phone = 'फ़ोन नंबर आवश्यक है (Phone number is required)';
      } else if (phoneDigits.length !== 10) {
        errors.phone = 'कृपया 10 अंकों का मान्य मोबाइल नंबर दर्ज करें (Valid 10-digit mobile number required)';
      }

      if (!formData.sameAsPhone && formData.whatsapp) {
        const waDigits = formData.whatsapp.replace(/\D/g, '');
        if (waDigits.length !== 10) {
          errors.whatsapp = 'कृपया 10 अंकों का मान्य व्हाट्सएप नंबर दर्ज करें';
        }
      }
    }

    if (step === 3) {
      if (!formData.city && !city) errors.city = 'कृपया शहर चुनें (City is required)';
      if (!formData.address.trim()) errors.address = 'दुकान या कार्यालय का पता आवश्यक है (Address is required)';
    }

    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) {
      showError('कृपया सभी आवश्यक फ़ील्ड्स सही ढंग से भरें');
      return;
    }

    setLoading(true);

    try {
      // Prepare payload structured for backend model
      const payload = {
        name: formData.name.trim(),
        contactPerson: formData.contactPerson.trim(),
        category: formData.category,
        subcategory: formData.subcategory || null,
        city: formData.city,
        village: formData.village || null,
        address: formData.address.trim(),
        landmark: formData.landmark.trim(),
        pincode: formData.pincode.trim(),
        phone: formData.phone.replace(/\D/g, ''),
        whatsapp: (formData.sameAsPhone ? formData.phone : formData.whatsapp).replace(/\D/g, ''),
        email: formData.email.trim(),
        website: formData.website.trim(),
        description: formData.description.trim(),
        tags: selectedTags,
        timings: {
          opening: formData.openingTime,
          closing: formData.closingTime,
          isOpenSunday: formData.isOpenSunday,
          closedOn: formData.isOpenSunday ? '' : 'Sunday'
        }
      };

      const response = await api.post('/businesses', payload);
      const createdBiz = response.data;
      const businessId = createdBiz._id;

      // Upload images if any
      if (images.length > 0) {
        const imageFormData = new FormData();
        images.forEach((img) => {
          if (img.file) {
            imageFormData.append('images', img.file);
          }
        });

        try {
          await api.post(`/businesses/${businessId}/images`, imageFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (imgError) {
          console.error('Error uploading images:', imgError);
        }
      }

      setSubmittedBusiness(createdBiz);
      setIsSuccess(true);
      showSuccess('बधाई! आपका बिज़नेस सफलतापूर्वक दर्ज कर लिया गया है।');
    } catch (error) {
      console.error('Error submitting business:', error);
      showError(error.response?.data?.message || 'सबमिशन में त्रुटि आई। कृपया पुनः प्रयास करें।');
    } finally {
      setLoading(false);
    }
  };

  const handleShareWhatsApp = () => {
    if (!submittedBusiness) return;
    const text = `मैंने अपना व्यवसाय *${formData.name}* Digital Fatehabad पोर्टल पर लिस्ट कर दिया है! आप भी अपनी दुकान या सर्विस को मुफ़्त में जोड़ें: https://digitalfatehabad.com`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleResetAndNew = () => {
    setIsSuccess(false);
    setSubmittedBusiness(null);
    setCurrentStep(1);
    setFormData(prev => ({
      name: '',
      contactPerson: '',
      category: '',
      subcategory: '',
      phone: '',
      whatsapp: '',
      sameAsPhone: true,
      email: '',
      website: '',
      openingTime: '09:00 AM',
      closingTime: '08:00 PM',
      isOpenSunday: true,
      city: prev.city,
      village: '',
      landmark: '',
      address: '',
      pincode: prev.pincode,
      description: ''
    }));
    setImages([]);
    setSelectedTags([]);
    setTagSearch('');
    setStepErrors({});
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Selected Category & Subcategory objects for preview
  const selectedCatObj = categories.find(c => c._id === formData.category);
  const selectedSubObj = subcategories.find(s => s._id === formData.subcategory);
  const selectedCityObj = cities.find(c => c._id === formData.city);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={handleClose}>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" />

      <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
        <div
          className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-gray-100 dark:border-gray-800 animate-scale-up"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top Modal Header */}
          <div className="bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 text-white px-6 py-5 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20">
                <Store className="w-5 h-5 text-yellow-300" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-black tracking-tight leading-tight">
                  दुकान / बिज़नेस जोड़ें
                </h2>
                <p className="text-xs text-primary-100 font-medium">
                  Add Your Business to Digital Fatehabad Directory
                </p>
              </div>
            </div>

            <button
              onClick={handleClose}
              disabled={loading}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all disabled:opacity-50"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* If Success Screen */}
          {isSuccess ? (
            <div className="p-8 sm:p-12 text-center flex-1 overflow-y-auto flex flex-col items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center justify-center mb-6 animate-bounce shadow-lg">
                <CheckCircle2 className="w-12 h-12" />
              </div>

              <h3 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2">
                बधाई हो! लिस्टिंग दर्ज हो गई है
              </h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-md mb-6 leading-relaxed">
                <strong className="text-primary-600 dark:text-primary-400 font-bold">{formData.name}</strong> का विवरण सफलतापूर्वक प्राप्त हो गया है। एडमिन समीक्षा के बाद यह शीघ्र ही पोर्टल पर लाइव हो जाएगा।
              </p>

              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-2xl p-4 max-w-md w-full border border-gray-200 dark:border-gray-700 text-left mb-8 space-y-1.5 text-xs sm:text-sm">
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>श्रेणी (Category):</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{selectedCatObj?.name || 'Local Service'}</span>
                </div>
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>फ़ोन नंबर:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">+91 {formData.phone}</span>
                </div>
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>स्थान (City/Town):</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{selectedCityObj?.name || 'Fatehabad'}</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
                <button
                  onClick={handleShareWhatsApp}
                  className="flex-1 py-3 px-4 bg-[#25D366] hover:bg-[#20ba59] text-white font-bold rounded-2xl flex items-center justify-center gap-2 transition-all shadow-md active:scale-95 text-sm"
                >
                  <Share2 className="w-4 h-4" /> WhatsApp पर शेयर करें
                </button>
                <button
                  onClick={handleResetAndNew}
                  className="py-3 px-4 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 font-semibold rounded-2xl transition-all text-sm"
                >
                  अन्य दुकान जोड़ें
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Stepper Progress Bar */}
              <div className="px-6 py-4 bg-gray-50/80 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
                <div className="flex items-center justify-between max-w-xl mx-auto relative">
                  {/* Connecting Line */}
                  <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 h-1 bg-gray-200 dark:bg-gray-700 -z-0 rounded-full" />
                  <div 
                    className="absolute top-1/2 left-0 -translate-y-1/2 h-1 bg-primary-600 transition-all duration-500 rounded-full"
                    style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
                  />

                  {STEPS.map((step) => {
                    const isCompleted = currentStep > step.id;
                    const isCurrent = currentStep === step.id;
                    const Icon = step.icon;

                    return (
                      <div key={step.id} className="relative z-10 flex flex-col items-center">
                        <button
                          type="button"
                          onClick={() => {
                            if (isCompleted) setCurrentStep(step.id);
                          }}
                          disabled={!isCompleted && !isCurrent}
                          className={`w-9 h-9 sm:w-10 sm:h-10 rounded-2xl flex items-center justify-center font-bold text-xs transition-all duration-300 shadow-sm ${
                            isCompleted
                              ? 'bg-green-600 text-white cursor-pointer hover:scale-105'
                              : isCurrent
                              ? 'bg-primary-600 text-white ring-4 ring-primary-100 dark:ring-primary-950 scale-105'
                              : 'bg-white dark:bg-gray-800 text-gray-400 border border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
                        </button>
                        <span className={`text-[11px] font-semibold mt-1 hidden sm:block ${
                          isCurrent ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500'
                        }`}>
                          {step.title}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Form Body with Multi-Step Views */}
              <div className="p-6 sm:p-8 overflow-y-auto flex-1">
                {/* STEP 1: Basic Information */}
                {currentStep === 1 && (
                  <div className="space-y-5 animate-fade-in">
                    <div>
                      <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                        दुकान / व्यवसाय का नाम (Business Name) *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="उदा. अमन ऑटो स्पेयर पार्ट्स, शर्मा मिष्ठान भंडार"
                        className={`w-full px-4 py-3 rounded-2xl text-sm bg-gray-50 dark:bg-gray-800/80 border text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${
                          stepErrors.name ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200 dark:border-gray-700'
                        }`}
                      />
                      {stepErrors.name && (
                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" /> {stepErrors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                        मालिक / संपर्क व्यक्ति का नाम (Owner / Contact Person)
                      </label>
                      <div className="relative">
                        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          name="contactPerson"
                          value={formData.contactPerson}
                          onChange={handleChange}
                          placeholder="उदा. राजेश कुमार, डॉ. सचिन मेहता"
                          className="w-full pl-10 pr-4 py-3 rounded-2xl text-sm bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                        />
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1">स्थानीय लोग अक्सर मालिक के नाम से भी दुकान पहचानते हैं।</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                          मुख्य श्रेणी (Main Category) *
                        </label>
                        <CustomSelect
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          options={categories}
                          placeholder="श्रेणी चुनें (उदा. Automotive, Health)"
                          required
                          icon={LayoutGrid}
                          className={stepErrors.category ? 'border-red-500' : ''}
                        />
                        {stepErrors.category && (
                          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5" /> {stepErrors.category}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                          उप-श्रेणी (Subcategory)
                        </label>
                        <CustomSelect
                          name="subcategory"
                          value={formData.subcategory}
                          onChange={handleChange}
                          options={subcategories}
                          placeholder={formData.category ? (subcategories.length ? 'उप-श्रेणी चुनें' : 'कोई उप-श्रेणी नहीं') : 'पहले मुख्य श्रेणी चुनें'}
                          disabled={!formData.category}
                          icon={Search}
                        />
                      </div>
                    </div>

                    {/* Tags Section */}
                    <div>
                      <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                        Tags / Keywords <span className="text-xs font-normal text-gray-500">(वैकल्पिक - Optional)</span>
                      </label>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                        अपनी सेवाओं से मिलते-जुलते टैग चुनें ताकि ग्राहक आसानी से ढूँढ सकें।
                      </p>

                      {/* Selected tag pills */}
                      {selectedTags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {selectedTags.map(tagId => {
                            const t = allTags.find(tg => tg._id === tagId);
                            if (!t) return null;
                            return (
                              <span key={tagId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary-600 text-white">
                                {t.name}
                                <button
                                  type="button"
                                  onClick={() => setSelectedTags(prev => prev.filter(id => id !== tagId))}
                                  className="ml-0.5 hover:text-primary-200"
                                >×</button>
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {/* Search input */}
                      <div className="relative mb-2">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          type="text"
                          value={tagSearch}
                          onChange={(e) => setTagSearch(e.target.value)}
                          placeholder="टैग खोजें (Search tags)..."
                          className="w-full pl-9 pr-9 py-2.5 rounded-2xl text-sm bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                        />
                        {tagSearch && (
                          <button
                            type="button"
                            onClick={() => setTagSearch('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* Tag suggestions */}
                      {(() => {
                        const q = tagSearch.toLowerCase().trim();
                        const catName = (categories.find(c => c._id === formData.category)?.name || '').toLowerCase();
                        const bizName = (formData.name || '').toLowerCase();

                        const filtered = allTags.filter(t => {
                          if (selectedTags.includes(t._id)) return false;
                          if (q) return t.name.toLowerCase().includes(q);
                          return true;
                        });

                        if (filtered.length === 0 && allTags.length > 0) {
                          return (
                            <div className="p-3 text-center text-sm text-gray-400 rounded-2xl border border-gray-200 dark:border-gray-700">
                              {q ? `"${q}" से मिलते टैग नहीं मिले` : 'सभी टैग चुन लिए गए'}
                            </div>
                          );
                        }
                        if (allTags.length === 0) return null;

                        const scored = filtered.map(t => {
                          const tn = t.name.toLowerCase();
                          let score = 0;
                          if (catName && (tn.includes(catName) || catName.includes(tn))) score += 3;
                          if (bizName) {
                            bizName.split(' ').forEach(w => {
                              if (w.length > 2 && tn.includes(w)) score += 2;
                            });
                          }
                          if (t.featured) score += 1;
                          return { ...t, score };
                        }).sort((a, b) => b.score - a.score);

                        const suggested = !q ? scored.filter(t => t.score > 0).slice(0, 8) : [];
                        const rest = !q ? scored.filter(t => t.score === 0) : scored;

                        return (
                          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800/80 max-h-48 overflow-y-auto">
                            {suggested.length > 0 && (
                              <>
                                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-950/30 border-b border-gray-100 dark:border-gray-800">
                                  ✦ {categories.find(c => c._id === formData.category)?.name || 'इस श्रेणी'} के लिए Suggested
                                </div>
                                <div className="flex flex-wrap gap-1.5 p-2.5 border-b border-gray-100 dark:border-gray-800">
                                  {suggested.map(tag => (
                                    <button
                                      key={tag._id}
                                      type="button"
                                      onClick={() => setSelectedTags(prev => [...prev, tag._id])}
                                      className="px-2.5 py-1 rounded-full text-xs font-semibold bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700 hover:bg-primary-100 dark:hover:bg-primary-900/60 transition"
                                    >
                                      + {tag.name}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                            {rest.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 p-2.5">
                                {rest.map(tag => (
                                  <button
                                    key={tag._id}
                                    type="button"
                                    onClick={() => setSelectedTags(prev => [...prev, tag._id])}
                                    className="px-2.5 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition border border-gray-200 dark:border-gray-700"
                                  >
                                    + {tag.name}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* STEP 2: Contact & Timings */}
                {currentStep === 2 && (
                  <div className="space-y-5 animate-fade-in">
                    <div>
                      <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                        फ़ोन / मोबाइल नंबर (Calling Number) *
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-400">
                          +91
                        </span>
                        <input
                          type="tel"
                          name="phone"
                          maxLength={10}
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="98XXXXXXXX (10 अंक)"
                          className={`w-full pl-14 pr-10 py-3 rounded-2xl text-sm bg-gray-50 dark:bg-gray-800/80 border text-gray-900 dark:text-white font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${
                            stepErrors.phone ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200 dark:border-gray-700'
                          }`}
                        />
                        {formData.phone.replace(/\D/g, '').length === 10 && (
                          <Check className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                        )}
                      </div>
                      {stepErrors.phone && (
                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" /> {stepErrors.phone}
                        </p>
                      )}
                    </div>

                    {/* WhatsApp Toggle & Field */}
                    <div className="p-4 rounded-2xl bg-green-50/60 dark:bg-green-950/20 border border-green-200/60 dark:border-green-900/40 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-bold text-green-800 dark:text-green-300">
                          <MessageCircle className="w-4 h-4 text-green-600" />
                          <span>व्हाट्सएप नंबर (WhatsApp Support)</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="sameAsPhone"
                            checked={formData.sameAsPhone}
                            onChange={handleChange}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                        </label>
                      </div>

                      {formData.sameAsPhone ? (
                        <p className="text-xs text-green-700 dark:text-green-400">
                          ✓ कॉलिंग नंबर (+91 {formData.phone || '...'}) ही व्हाट्सएप नंबर के रूप में उपयोग होगा।
                        </p>
                      ) : (
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            अलग व्हाट्सएप नंबर दर्ज करें
                          </label>
                          <input
                            type="tel"
                            name="whatsapp"
                            maxLength={10}
                            value={formData.whatsapp}
                            onChange={handleChange}
                            placeholder="अलग 10-अंकीय व्हाट्सएप नंबर"
                            className="w-full px-4 py-2.5 rounded-xl text-sm bg-white dark:bg-gray-800 border border-green-300 dark:border-green-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                      )}
                    </div>

                    {/* Timings & Sunday status */}
                    <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-gray-200">
                        <Clock className="w-4 h-4 text-primary-500" />
                        <span>दुकान खुलने व बंद होने का समय (Business Hours)</span>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">खुलने का समय (Opening)</label>
                          <select
                            name="openingTime"
                            value={formData.openingTime}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-xs sm:text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none"
                          >
                            {['06:00 AM', '07:00 AM', '08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM'].map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-500 mb-1">बंद होने का समय (Closing)</label>
                          <select
                            name="closingTime"
                            value={formData.closingTime}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-xs sm:text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none"
                          >
                            {['06:00 PM', '07:00 PM', '08:00 PM', '09:00 PM', '10:00 PM', '11:00 PM'].map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-1">
                        <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">
                          रविवार को दुकान खुली रहती है? (Open on Sunday?)
                        </span>
                        <input
                          type="checkbox"
                          name="isOpenSunday"
                          checked={formData.isOpenSunday}
                          onChange={handleChange}
                          className="w-4 h-4 text-primary-600 rounded"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                          ईमेल (Email - Optional)
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="shop@example.com"
                          className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                          वेबसाइट (Website - Optional)
                        </label>
                        <input
                          type="url"
                          name="website"
                          value={formData.website}
                          onChange={handleChange}
                          placeholder="https://..."
                          className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 3: Location & Address */}
                {currentStep === 3 && (
                  <div className="space-y-5 animate-fade-in">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                          शहर / कस्बा (City / Town) *
                        </label>
                        <CustomSelect
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          options={cities}
                          placeholder={city ? `Auto-detected: ${city}` : 'शहर चुनें'}
                          required={!city}
                          disabled={!!city}
                          icon={MapPin}
                          className={stepErrors.city ? 'border-red-500' : ''}
                        />
                        {stepErrors.city && (
                          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5" /> {stepErrors.city}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                          गांव (Village - यदि दुकान गांव में है)
                        </label>
                        <CustomSelect
                          name="village"
                          value={formData.village}
                          onChange={handleChange}
                          options={villages}
                          placeholder="गांव चुनें (वैकल्पिक)"
                          icon={Compass}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                        नजदीकी लैंडमार्क / मार्केट (Landmark / Market Area)
                      </label>
                      <input
                        type="text"
                        name="landmark"
                        value={formData.landmark}
                        onChange={handleChange}
                        placeholder="उदा. पुरानी अनाज मंडी के पास, बस स्टैंड के सामने, जीटी रोड"
                        className="w-full px-4 py-3 rounded-2xl text-sm bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">लैंडमार्क से ग्राहकों को आपकी दुकान ढूँढने में आसानी होती है।</p>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-2">
                        <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                          पूरा पता (Full Address) *
                        </label>
                        <textarea
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="दुकान नंबर, गली, मोहल्ला, रोड..."
                          rows={2}
                          className={`w-full px-4 py-2.5 rounded-2xl text-sm bg-gray-50 dark:bg-gray-800/80 border text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            stepErrors.address ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200 dark:border-gray-700'
                          }`}
                        />
                        {stepErrors.address && (
                          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5" /> {stepErrors.address}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                          पिनकोड (Pincode)
                        </label>
                        <input
                          type="text"
                          name="pincode"
                          maxLength={6}
                          value={formData.pincode}
                          onChange={handleChange}
                          placeholder="125050"
                          className="w-full px-4 py-2.5 rounded-2xl text-sm bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 4: Photos, Description & Live Preview */}
                {currentStep === 4 && (
                  <div className="space-y-6 animate-fade-in">
                    <div>
                      <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                        व्यवसाय का विवरण (About Business / Services)
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={3}
                        placeholder="अपनी दुकान के मुख्य उत्पादों या सेवाओं का विवरण लिखें..."
                        className="w-full px-4 py-2.5 rounded-2xl text-sm bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-primary-500" />
                        <span>दुकान या बोर्ड की फोटो (Upload Photos - Optional)</span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        अधिकतम 10 फोटो अपलोड कर सकते हैं। पहली फोटो मुख्य कार्ड पर दिखेगी।
                      </p>
                      <ImageUpload
                        images={images}
                        onImagesChange={setImages}
                        maxImages={10}
                      />
                    </div>

                    {/* LIVE CARD PREVIEW */}
                    <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
                      <div className="text-xs font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400 mb-3 flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4" /> लाइव कार्ड प्रीव्यू (यह आपकी वेबसाइट पर ऐसा दिखेगा)
                      </div>

                      <div className="bg-white dark:bg-gray-800 rounded-3xl p-5 border-2 border-primary-100 dark:border-primary-900/40 shadow-lg max-w-md mx-auto">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div>
                            <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-primary-50 dark:bg-primary-950/60 text-primary-600 dark:text-primary-300 border border-primary-200/50">
                              {selectedCatObj?.name || 'Category'}
                            </span>
                            {selectedSubObj && (
                              <span className="text-[11px] font-medium ml-1.5 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                {selectedSubObj.name}
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] font-semibold text-green-600 bg-green-50 dark:bg-green-950/50 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {formData.isOpenSunday ? 'Sun Open' : 'Closed Sun'}
                          </span>
                        </div>

                        <h4 className="text-lg font-black text-gray-900 dark:text-white mt-1">
                          {formData.name || 'आपकी दुकान का नाम'}
                        </h4>

                        {formData.contactPerson && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                            सम्पर्क: {formData.contactPerson}
                          </p>
                        )}

                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-2 flex items-start gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-primary-500 flex-shrink-0 mt-0.5" />
                          <span>
                            {[formData.landmark, formData.address, selectedCityObj?.name].filter(Boolean).join(', ') || 'फतेहाबाद, हरियाणा'}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs">
                          <div className="flex items-center gap-1.5 font-bold text-gray-800 dark:text-gray-200">
                            <Phone className="w-3.5 h-3.5 text-primary-500" />
                            <span>+91 {formData.phone || '98XXXXXXXX'}</span>
                          </div>
                          <div className="flex items-center gap-1.5 font-bold text-green-600">
                            <MessageCircle className="w-3.5 h-3.5" />
                            <span>WhatsApp Active</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom Navigation Buttons */}
              <div className="px-6 py-4 bg-gray-50/90 dark:bg-gray-850 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between flex-shrink-0 gap-3">
                {currentStep > 1 ? (
                  <button
                    type="button"
                    onClick={handlePrev}
                    disabled={loading}
                    className="px-5 py-2.5 rounded-2xl text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 transition-all flex items-center gap-1.5"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={loading}
                    className="px-5 py-2.5 rounded-2xl text-sm font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Cancel
                  </button>
                )}

                {currentStep < 4 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-1.5 active:scale-95"
                  >
                    <span>Next</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-8 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-2xl text-sm font-black shadow-lg hover:shadow-xl transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>दर्ज हो रहा है...</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5" />
                        <span>सबमिट करें (Confirm & Submit)</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubmitBusinessModal;
